import I18n from 'I18n';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Link } from 'foundations-components/transitional/components/Link';
import { Small } from 'foundations-components/transitional/components/Small';
import { StepperInput } from 'foundations-components/transitional/components/StepperInput';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import styled from 'foundations-theming/utils';
import { getI18nText } from 'pricing-pages-lib/utils/languageUtils';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { PriceDisplay } from 'pricing-pages-ui-components/price/PriceDisplay';
import { useEffect, useId, useState } from 'react';
import { SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import { useDisplayedCurrencyCode } from '../../../hooks/useCurrencies';
import { useAppSelector } from '../../../reducers';
import { calculatedPriceForProductConfigurationsInListSelector } from '../../../selectors/PricingSelectors';
import { hasProductInProductInventorySelector, productsFromProductInventoryQuantityPackQuantitySelector, productsFromProductInventoryQuantitySelector } from '../../../selectors/ProductInventorySelectors';
import { productForSkuIdSelector, skuIdIsQuantityPackSelector, skuSelector } from '../../../selectors/ProductsSelectors';
import { trackAdditionalUnitsPicker, trackModalInteraction } from '../../../utils/tracking/trackingEventUtils';
import PaidUsersModal from './PaidUsersModal';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledInlineTitle = styled(FormattedMessage).withConfig({
  displayName: "SeatsPickerMerchCardPreSeats__StyledInlineTitle",
  componentId: "sc-15uvmpj-0"
})(["font-weight:600;font-size:14px;"]);
const SeatsPickerMerchCardPreSeats = ({
  additionalUnitSkuId,
  additionalUnitQuantity,
  onChange,
  productConfigurations,
  trackingSource,
  queryParamTerm,
  isLoadingQuantityPacks
}) => {
  const isQuantityPack = useAppSelector(state => skuIdIsQuantityPackSelector(state, {
    skuId: additionalUnitSkuId
  }));
  const {
    includedWithProductVersion,
    quantityPackApiName,
    productVersionSkuId
  } = useAppSelector(state => {
    if (!isQuantityPack) {
      return {
        includedWithProductVersion: 0,
        quantityPackApiName: null,
        productVersionSkuId: additionalUnitSkuId
      };
    } else {
      const quantityPackSku = skuSelector(state, {
        skuId: additionalUnitSkuId
      });
      const {
        includedWithProductVersion: quantityPackIncludedwithProductVersion,
        quantityPackApiName: quantityPackApiNameScope,
        productVersionSkuId: quantityPackProductVersionSkuId
      } = quantityPackSku;
      return {
        includedWithProductVersion: quantityPackIncludedwithProductVersion,
        quantityPackApiName: quantityPackApiNameScope,
        productVersionSkuId: quantityPackProductVersionSkuId
      };
    }
  });
  const {
    apiName,
    productLine,
    productLevel
  } = useAppSelector(state => productForSkuIdSelector(state, {
    skuId: productVersionSkuId
  }));
  const currencyCode = useDisplayedCurrencyCode();
  const ownedQuantity = useAppSelector(state => quantityPackApiName ? productsFromProductInventoryQuantityPackQuantitySelector(state, {
    productIdentifier: {
      apiName
    },
    quantityPackApiName
  }) : productsFromProductInventoryQuantitySelector(state, {
    skuId: additionalUnitSkuId
  }));
  const minPurchasableSeats = includedWithProductVersion + ownedQuantity;
  const ownsProduct = useAppSelector(state => hasProductInProductInventorySelector(state, {
    productAttributeFilters: {
      apiName
    }
  }));
  const ownedUnitQuantity = ownsProduct ? minPurchasableSeats : 0;
  const [paidUsersModalOpen, setPaidUsersModalOpen] = useState(false);
  const handlePaidUsersLinkClick = () => {
    setPaidUsersModalOpen(true);
    trackModalInteraction({
      action: 'click to open',
      name: 'paid-vs-free-users-modal',
      value: getI18nText('pricingPage.seatsPicker.howMany', {
        locale: 'en-us'
      }),
      productLine,
      productLevel,
      trackingSource
    });
  };
  useEffect(() => {
    if (additionalUnitQuantity < ownedQuantity) {
      onChange(ownedQuantity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalUnitQuantity, ownedQuantity]);
  const additionalUnitPrice = useAppSelector(state => calculatedPriceForProductConfigurationsInListSelector(state, {
    allProductConfigurations: productConfigurations,
    productConfigurations: [createProductConfig({
      skuId: additionalUnitSkuId
    })],
    queryParamTerm,
    displayedCurrencyCode: currencyCode
  }).monthly.calculatedPrice);
  const roundValue = ({
    target: {
      value
    }
  }) => {
    const roundedValue = parseInt(value, 10);
    let nextAdditionalUnitQuantity = roundedValue < minPurchasableSeats ? minPurchasableSeats : roundedValue;
    nextAdditionalUnitQuantity = nextAdditionalUnitQuantity - includedWithProductVersion;
    onChange(nextAdditionalUnitQuantity);
  };
  const handleSeatsChange = event => {
    if (event.target.value) {
      const oldQuantity = additionalUnitQuantity + includedWithProductVersion;
      const newQuantity = parseInt(event.target.value, 10);
      trackAdditionalUnitsPicker({
        action: `stepper ${newQuantity > oldQuantity ? 'increase' : 'decrease'}`,
        trackingSource,
        productLine,
        productLevel,
        ownsProduct,
        skuId: additionalUnitSkuId,
        ownedUnitQuantity,
        displayedUnitQuantity: newQuantity
      });
    }
    roundValue(event);
  };
  const SeatsPickerMerchCardPreSeatsTitleId = useId();
  const SeatsPickerMerchCardPreSeatsSubtextId = useId();
  const ariaDescribedBy = [SeatsPickerMerchCardPreSeatsTitleId, SeatsPickerMerchCardPreSeatsSubtextId].join(' ');

  // productLine will be filled in for a la carte hubs and unbundled suite
  // For TB sku, there's no productLine. Use quantityPack's quantityPackApiName to effectively
  // get its productLine
  const productLineKey = productLine || (quantityPackApiName === 'additional-sales-seats' ? SALES : SERVICE);
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx("span", {
      id: SeatsPickerMerchCardPreSeatsTitleId,
      children: /*#__PURE__*/_jsx(StyledInlineTitle, {
        message: `pricingPage.seatsPicker.title.${productLevel.toLowerCase()}`
      })
    }), /*#__PURE__*/_jsxs(TFlex, {
      direction: "row",
      justify: "between",
      align: "center",
      children: [/*#__PURE__*/_jsxs(Small, {
        children: [/*#__PURE__*/_jsx("span", {
          id: SeatsPickerMerchCardPreSeatsSubtextId,
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "pricingPage.seatsPicker.compactSubtext_jsx",
            elements: {
              PriceDisplay
            },
            options: {
              currencyCode,
              price: additionalUnitPrice
            }
          })
        }), /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(Small, {
            children: /*#__PURE__*/_jsx(Link, {
              onClick: handlePaidUsersLinkClick,
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "pricingPage.seatsPicker.howMany"
              })
            })
          })
        })]
      }), paidUsersModalOpen && /*#__PURE__*/_jsx(PaidUsersModal, {
        productLevel: productLevel,
        productLine: productLineKey,
        onCloseClick: () => setPaidUsersModalOpen(false)
      }), /*#__PURE__*/_jsx(StepperInput, {
        "aria-label": I18n.text('pricingPage.seatsPicker.a11y.paidUsers'),
        "aria-describedby": ariaDescribedBy,
        minInlineSize: "60px",
        inlineSize: "100px",
        value: additionalUnitQuantity + includedWithProductVersion,
        min: minPurchasableSeats,
        onChange: handleSeatsChange,
        disabled: isLoadingQuantityPacks,
        "data-selenium-class": "seats-picker-input",
        "data-selenium-id": `${trackingSource}-${apiName}-seat-card-stepper-input`
      })]
    })]
  });
};
export default SeatsPickerMerchCardPreSeats;